/* BPC quantity validation. Keep the theme's existing cart request/drawer code. */
(() => {
  if (window.BpcQuantityRules) return;

  function positive(value, fallback) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : fallback;
  }

  // form.elements includes fields outside the form linked with form="...".
  function quantityInput(form) {
    if (!form) return undefined;
    return Array.from(form.elements).find((field) => field.name === 'quantity' && !field.disabled);
  }

  function rules(input) {
    const maximum = input.dataset.max;
    return {
      min: positive(input.dataset.min ?? input.getAttribute('min'), 1),
      step: positive(input.getAttribute('step'), 1),
      max: maximum === undefined
        ? positive(input.getAttribute('max'), null)
        : positive(maximum, null),
      cart: Math.max(0, Number(input.dataset.cartQuantity) || 0),
      // The theme calculates these remaining limits after accounting for the cart.
      addMin: positive(input.getAttribute('min'), 1),
      addMax: input.hasAttribute('max') ? Math.max(0, Number(input.getAttribute('max'))) : null,
    };
  }

  function describe(rule) {
    const parts = [`Minimum order quantity: ${rule.min}.`];
    if (rule.step > 1) parts.push(`Order in multiples of ${rule.step}.`);
    if (rule.max !== null) parts.push(`Maximum order quantity: ${rule.max}.`);
    if (rule.cart > 0) parts.push(`You already have ${rule.cart} in your basket.`);
    return parts.join(' ');
  }

  function validate(input) {
    const rule = rules(input);
    const value = input.value.trim();
    const quantity = Number(value);
    const context = describe(rule);
    if (!value || !Number.isSafeInteger(quantity) || quantity < 1) {
      return `Please enter a whole-number quantity of at least 1. ${context}`;
    }
    const total = rule.cart + quantity;
    if (total < rule.min || quantity < rule.addMin) {
      return `Please add at least ${Math.max(rule.addMin, rule.min - rule.cart)}. ${context}`;
    }
    if ((rule.max !== null && total > rule.max) || (rule.addMax !== null && quantity > rule.addMax)) {
      return `This quantity exceeds the amount you can add. ${context}`;
    }
    if (total % rule.step !== 0) {
      return `This quantity does not meet the pack-size rule. ${context}`;
    }
    return '';
  }

  function showError(productForm, message, input) {
    const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = !message;
    }
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  // Append rule information to Shopify/app errors without replacing their cause.
  function wrapErrors(productForm) {
    if (productForm.bpcErrorsWrapped || typeof productForm.handleErrorMessage !== 'function') return;
    const original = productForm.handleErrorMessage;
    productForm.handleErrorMessage = function (message = false) {
      const input = quantityInput(this.querySelector('form'));
      if (message && input) {
        const rule = rules(input);
        if (rule.min > 1 || rule.step > 1 || rule.max !== null) message = `${message} ${describe(rule)}`;
      }
      return original.call(this, message);
    };
    productForm.bpcErrorsWrapped = true;
  }

  window.BpcQuantityRules = { validate, rules, describe };

  // Capture runs before the product-form's AJAX submit handler.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const productForm = form.closest('product-form');
    if (!productForm || !form.matches('form[data-type="add-to-cart-form"]')) return;
    wrapErrors(productForm);
    const input = quantityInput(form);
    if (!input) return; // Gift cards and forms with Shopify's default quantity.
    const message = validate(input);
    if (message) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showError(productForm, message, input);
      input.focus();
    } else {
      showError(productForm, '', input);
    }
  }, true);

  // Ensure only the selected quantity is encoded in FormData, including external inputs.
  document.addEventListener('formdata', (event) => {
    const form = event.target;
    if (!form.matches('form[data-type="add-to-cart-form"]') || !form.closest('product-form')) return;
    const input = quantityInput(form);
    if (input) event.formData.set('quantity', input.value);
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (input.name !== 'quantity' || !input.form || input.getAttribute('aria-invalid') !== 'true') return;
    const productForm = input.form.closest('product-form');
    if (productForm) showError(productForm, '', input);
  });
})();
